import { useUser } from '../../../Context/useUser';
import { Link, useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { useState, useEffect, useMemo } from 'react';
import API from '../../../Utils/api';
import styles from './PerformancePage.module.css';

import { FaChartLine, FaPlus, FaColumns } from 'react-icons/fa';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import PerformanceChart from './PerformanceChart';
import ReadinessChart from './ReadinessChart';
import PerformanceTable, { Glossary } from './PerformanceTable';
import { EARLIEST_DATE, getLoadBaseline, isValidDateRange } from './readinessUtils';
import { getAcwrWarmupWindows } from './acwrWarmup';

const combineWorkoutsForSameDay = workouts => {
    const workoutsByDate = workouts.reduce((acc, workout) => {
        const date = new Date(workout.workoutStart).toLocaleDateString('el-GR');

        if (!acc[date]) {
            acc[date] = {
                workoutStart: new Date(workout.workoutStart).setHours(0, 0, 0, 0),
                workoutEnd: new Date(workout.workoutStart).setHours(23, 59, 59, 999),
                trainingDurationMin: 0,
                trainingDurationSec: 0,
                volume: 0,
                css: workout.css,
                ftp: workout.ftp,
                nss: workout.nss,
                if: 0,
                tl: 0,
                tss: 0,
                atl: workout.atl,
                ctl: workout.ctl,
                tsb: workout.tsb,
                acwr: workout.acwr,
                workoutCount: 0
            };
        }

        const current = acc[date];
        current.trainingDurationMin += workout.trainingDurationMin || 0;
        current.trainingDurationSec += workout.trainingDurationSec || 0;
        current.volume += workout.volume || 0;
        current.tl += workout.tl || 0;
        current.tss += workout.tss || 0;
        current.if =
            (current.if * current.workoutCount + (workout.if || 0)) / (current.workoutCount + 1);
        current.workoutCount += 1;

        current.atl = workout.atl;
        current.ctl = workout.ctl;
        current.tsb = workout.tsb;
        current.acwr = workout.acwr;
        current.css = workout.css;
        current.ftp = workout.ftp;
        current.nss = workout.nss;

        return acc;
    }, {});

    // Convert the grouped workouts back to an array
    return Object.entries(workoutsByDate).map(([date, workout]) => ({
        ...workout,
        trainingDurationMin:
            workout.trainingDurationMin + Math.floor(workout.trainingDurationSec / 60),
        trainingDurationSec: workout.trainingDurationSec % 60
    }));
};

const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
};

function calculateTrainingLoadFrontend(prev, tss, daysSinceLast) {
    let atl = 0;
    let ctl = 0;

    if (prev == null) {
        atl = tss / 7;
        ctl = tss / 28;
    } else if (daysSinceLast === 0 || daysSinceLast === 1) {
        atl = prev.atl + (tss - prev.atl) * (1 / 7);
        ctl = prev.ctl + (tss - prev.ctl) * (1 / 28);
    } else {
        let yesterdayATL = prev.atl;
        let yesterdayCTL = prev.ctl;
        for (let i = 1; i < daysSinceLast; i++) {
            yesterdayATL = yesterdayATL + (0 - yesterdayATL) * (1 / 7);
            yesterdayCTL = yesterdayCTL + (0 - yesterdayCTL) * (1 / 28);
        }
        atl = yesterdayATL + (tss - yesterdayATL) * (1 / 7);
        ctl = yesterdayCTL + (tss - yesterdayCTL) * (1 / 28);
    }
    const tsb = ctl - atl;
    const acwr = ctl ? atl / ctl : 0;
    return { atl, ctl, tsb, acwr };
}

// Empty State Component
const EmptyState = ({ currentUser, calendarPath }) => (
    <div className={styles.emptyState}>
        <FaChartLine className={styles.emptyStateIcon} />
        <h3 className={styles.emptyStateTitle}>No Performance Data Available</h3>
        <p className={styles.emptyStateMessage}>
            {currentUser?.firstName || 'The athlete'} hasn't completed any workouts yet. Performance
            analysis will be available once workouts are logged and completed.
        </p>
        <Link to={calendarPath} className={styles.emptyStateAction}>
            <FaPlus style={{ marginRight: 'var(--space-sm)' }} />
            Add Your First Workout
        </Link>
    </div>
);

export default function PerformancePage({
    showTable = true,
    showPerformanceChart = true,
    showReadinessChart = true,
    title = 'Performance Analysis',
    showTitle = true
}) {
    const { user } = useUser();
    const { athleteId } = useParams();
    const visitedUser = useVisitingUser(athleteId);
    const currentUser = athleteId ? visitedUser : user;
    // A coach visiting an athlete stays on that athlete's routes; there is no coach-level /calendar.
    const calendarPath = athleteId ? `/athlete/${athleteId}/calendar` : '/calendar';

    const [data, setData] = useState([]);
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [showEmptyDays, setShowEmptyDays] = useState(true);
    const [showSideBySide, setShowSideBySide] = useState(false);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;
        const { data: performances } = await API.getAllUserWorkoutTypes(currentUser._id);
        setData(
            performances
                .filter(workout => workout.status === 'Completed' || workout.status === 'Assigned')
                .sort((a, b) => new Date(b.workoutStart) - new Date(a.workoutStart))
                .reverse()
        );
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    useEffect(() => {
        handleShortcutClick('month');
    }, []);

    const { filteredData } = useMemo(() => {
        let workouts = data;
        let startDate, endDate;

        // Only filter if both dates are provided and valid
        if (isValidDateRange(dateRange.startDate, dateRange.endDate)) {
            startDate = new Date(dateRange.startDate);
            endDate = new Date(dateRange.endDate);
            endDate.setHours(23, 59, 59);

            workouts = data.filter(workout => {
                const workoutDate = new Date(workout.workoutStart);
                return workoutDate >= startDate && workoutDate <= endDate;
            });
        } else {
            startDate = new Date(Math.min(...data.map(w => new Date(w.workoutStart))));
            endDate = new Date(Math.max(...data.map(w => new Date(w.workoutStart))));
        }

        const allDates = getDatesInRange(startDate, endDate);
        // Separate completed and assigned workouts
        const completedWorkouts = workouts.filter(w => w.status === 'Completed');
        const assignedWorkouts = workouts.filter(w => w.status === 'Assigned');
        const combinedWorkouts = combineWorkoutsForSameDay(completedWorkouts);
        const workoutsByDate = combinedWorkouts.reduce((acc, workout) => {
            const dateStr = new Date(workout.workoutStart).toLocaleDateString('el-GR');
            acc[dateStr] = workout;
            return acc;
        }, {});

        // Add assigned workouts to the map (for planning purposes)
        assignedWorkouts.forEach(workout => {
            const dateStr = new Date(workout.workoutStart).toLocaleDateString('el-GR');
            if (!workoutsByDate[dateStr]) {
                workoutsByDate[dateStr] = {
                    workoutStart: new Date(workout.workoutStart).setHours(0, 0, 0, 0),
                    workoutEnd: new Date(workout.workoutStart).setHours(23, 59, 59, 999),
                    trainingDurationMin: workout.trainingDurationMin || 0,
                    trainingDurationSec: workout.trainingDurationSec || 0,
                    volume: workout.volume || 0,
                    css: workout.css || 1.0,
                    ftp: workout.ftp || 0,
                    nss: workout.nss || 0,
                    if: 0,
                    tl: 0,
                    tss: 0,
                    atl: null,
                    ctl: null,
                    tsb: null,
                    acwr: null,
                    workoutCount: 0,
                    status: 'Assigned',
                    isAssigned: true
                };
            } else {
                workoutsByDate[dateStr].isAssigned = true;
                workoutsByDate[dateStr].assignedVolume =
                    (workoutsByDate[dateStr].assignedVolume || 0) + (workout.volume || 0);
            }
        });

        // A range opening on rest days continues from the preceding completed workout
        // rather than from zero load.
        let { prev, lastWorkoutDate } = getLoadBaseline(data, startDate);
        const mapped = allDates.map(date => {
            const dateStr = date.toLocaleDateString('el-GR');
            let entry;
            if (workoutsByDate[dateStr]) {
                entry = { ...workoutsByDate[dateStr] };
                // Use server-calculated ATL/CTL/TSB for workout days — they account for full
                // training history, not just the selected date range.
                if (entry.tss > 0 || entry.workoutCount > 0) {
                    prev = { atl: entry.atl, ctl: entry.ctl };
                    lastWorkoutDate = date;
                }
            } else {
                const tss = 0;
                const daysSinceLast =
                    lastWorkoutDate == null
                        ? null
                        : Math.round((date - lastWorkoutDate) / (1000 * 60 * 60 * 24));
                const { atl, ctl, tsb, acwr } = calculateTrainingLoadFrontend(
                    prev,
                    tss,
                    daysSinceLast == null ? 0 : daysSinceLast
                );
                entry = {
                    workoutStart: date.setHours(0, 0, 0, 0),
                    workoutEnd: date.setHours(23, 59, 59, 999),
                    trainingDurationMin: 0,
                    trainingDurationSec: 0,
                    volume: 0,
                    css: combinedWorkouts[combinedWorkouts.length - 1]?.css || 1.0,
                    ftp: combinedWorkouts[combinedWorkouts.length - 1]?.ftp || 0,
                    nss: combinedWorkouts[combinedWorkouts.length - 1]?.nss || 0,
                    if: 0,
                    tl: 0,
                    tss: 0,
                    atl,
                    ctl,
                    tsb,
                    acwr,
                    workoutCount: 0
                };
            }
            return entry;
        });
        return { filteredData: mapped };
    }, [data, dateRange]);

    const chartDataSource = showEmptyDays
        ? filteredData
        : filteredData.filter(w => w.workoutCount > 0);

    // ACWR warm-up windows come from the full workout history, never the selected range.
    const acwrWarmupWindows = useMemo(() => getAcwrWarmupWindows(data), [data]);

    // @George on purpose i did period and not months or days because we are able to utilize
    // the native Date API to set the dates because it is more accurate and easier to handle
    const handleShortcutClick = period => {
        const today = new Date();
        const endDate = today.toISOString().split('T')[0];
        const start = new Date(today);
        switch (period) {
            case 'year':
                start.setFullYear(start.getFullYear() - 1);
                break;
            case '6months':
                start.setMonth(start.getMonth() - 6);
                break;
            case 'month':
                start.setMonth(start.getMonth() - 1);
                break;
            case 'week':
                start.setDate(start.getDate() - 7);
                break;
            default:
                break;
        }
        const startDate = start.toISOString().split('T')[0];
        setDateRange({ startDate, endDate });
    };

    // Check if there's any actual workout data
    const hasWorkoutData = data.length > 0;
    const hasFilteredData = filteredData.some(
        entry => entry.workoutCount > 0 || entry.isAssigned || entry.status === 'Assigned'
    );

    return (
        <div className={styles.pageWrap}>
            {showTitle && <h4 className={styles.title + ' island'}> {title}</h4>}

            {hasWorkoutData ? (
                <>
                    <div className={styles.dateFilterWrap + ' island'}>
                        <div className={styles.dateInputs}>
                            <div>
                                <label htmlFor='startDate'>Start Date:</label>
                                <input
                                    type='date'
                                    id='startDate'
                                    min={EARLIEST_DATE}
                                    value={dateRange.startDate}
                                    onChange={e => {
                                        setDateRange(prev => ({
                                            ...prev,
                                            startDate: e.target.value
                                        }));
                                    }}
                                />
                            </div>
                            <div>
                                <label htmlFor='endDate'>End Date:</label>
                                <input
                                    type='date'
                                    id='endDate'
                                    min={dateRange.startDate || EARLIEST_DATE}
                                    value={dateRange.endDate}
                                    onChange={e => {
                                        setDateRange(prev => ({
                                            ...prev,
                                            endDate: e.target.value
                                        }));
                                    }}
                                />
                            </div>
                            <div className={styles.shortcutButtons}>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('year')}
                                >
                                    1 Year
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('6months')}
                                >
                                    6 Months
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('month')}
                                >
                                    1 Month
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('week')}
                                >
                                    1 Week
                                </button>
                                <button
                                    type='button'
                                    className={styles.eyeToggleBtn}
                                    title={showEmptyDays ? 'Hide empty days' : 'Show empty days'}
                                    onClick={() => setShowEmptyDays(v => !v)}
                                >
                                    {showEmptyDays ? <FaEye /> : <FaEyeSlash />}
                                </button>
                            </div>
                            {(dateRange.startDate || dateRange.endDate) &&
                                !isValidDateRange(dateRange.startDate, dateRange.endDate) && (
                                    <div className={styles.dateRangeError}>
                                        Please select a valid date range (end after start, from 2020 onwards)
                                    </div>
                                )}
                        </div>
                    </div>

                    {showTable && (
                        <div className={styles.dataTablesWrap + ' island'}>
                            {hasFilteredData ? (
                                <PerformanceTable
                                    data={data}
                                    dateRange={dateRange}
                                    isValidDateRange={isValidDateRange}
                                />
                            ) : (
                                <EmptyState currentUser={currentUser} calendarPath={calendarPath} />
                            )}
                        </div>
                    )}

                    {showPerformanceChart &&
                    showReadinessChart &&
                    hasFilteredData &&
                    showSideBySide ? (
                        <div className={styles.chartsWrap + ' island'}>
                            <button
                                type='button'
                                className={styles.sideBySideToggle}
                                title='Toggle side-by-side view'
                                onClick={() => setShowSideBySide(v => !v)}
                            >
                                <FaColumns />
                            </button>
                            <div className={styles.sideBySideCharts}>
                                <div className={styles.halfChart}>
                                    <PerformanceChart chartDataSource={chartDataSource} />
                                </div>
                                <div className={styles.halfChart}>
                                    <ReadinessChart
                                        chartDataSource={chartDataSource}
                                        filteredData={filteredData}
                                        acwrWarmupWindows={acwrWarmupWindows}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {(showPerformanceChart || showReadinessChart) && hasFilteredData && (
                                <div className={styles.chartsWrap + ' island'}>
                                    {showPerformanceChart && showReadinessChart && (
                                        <button
                                            type='button'
                                            className={styles.sideBySideToggle}
                                            title='Toggle side-by-side view'
                                            onClick={() => setShowSideBySide(v => !v)}
                                        >
                                            <FaColumns />
                                        </button>
                                    )}
                                    {showPerformanceChart && (
                                        <PerformanceChart chartDataSource={chartDataSource} />
                                    )}
                                    {showReadinessChart && (
                                        <ReadinessChart
                                            chartDataSource={chartDataSource}
                                            filteredData={filteredData}
                                            acwrWarmupWindows={acwrWarmupWindows}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {hasFilteredData && (
                        <div className='island' style={{ width: '100%' }}>
                            <Glossary />
                        </div>
                    )}
                </>
            ) : (
                <EmptyState currentUser={currentUser} calendarPath={calendarPath} />
            )}
        </div>
    );
}
